// components/ai/AIDashboard.tsx
// Combined overview of all AI usage + Ask Omore interactions

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StatsCard } from '../StatsCard';
import { fetchAIUsageDashboard } from '../../services/aiUsageService';
import { useAlerts } from '../../contexts/AlertContext';
import { AIFeatureCategoryInfo } from '../../types/AIUsageTypes';
import type { CombinedAIDashboard } from '../../types/AIUsageTypes';
import { AlertPanel } from '../alerts/AlertPanel';
import './AIDashboard.css';

export const AIDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboard, setDashboard] = useState<CombinedAIDashboard | null>(null);
    const [trendView, setTrendView] = useState<'calls' | 'tokens'>('calls');
    const { unacknowledgedCount, setAlertPanelOpen } = useAlerts();

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchAIUsageDashboard();
            setDashboard(data);
        } catch (err: any) {
            console.error('Error loading AI dashboard:', err);
            setError(err.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    // Compute max value in trend for bar scaling
    const trendData = dashboard
        ? (trendView === 'calls' ? dashboard.aiUsage.callsTrend : dashboard.aiUsage.tokensTrend)
        : [];
    const trendMax = trendData.reduce((max, d) => Math.max(max, d.count), 1);

    // Compute max for omore sessions trend
    const omoreTrendMax = dashboard
        ? Math.max(...dashboard.omoreInteractions.sessionsTrend.map(s => s.count), 1)
        : 1;

    return (
        <div className="ai-dashboard-container">
            <header className="ai-dashboard-header">
                <div className="header-content">
                    <div>
                        <h1>AI & Analytics Dashboard</h1>
                        <p className="header-subtitle">Combined overview of all AI usage and Ask Omore interactions</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/dashboard')} className="back-button">← Back to Dashboard</button>
                        <button
                            onClick={() => setAlertPanelOpen(true)}
                            className="alert-badge-button"
                            style={{
                                padding: '8px 16px',
                                background: unacknowledgedCount > 0 ? '#e53e3e' : '#e2e8f0',
                                color: unacknowledgedCount > 0 ? 'white' : '#2d3748',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Alerts {unacknowledgedCount > 0 ? `(${unacknowledgedCount})` : ''}
                        </button>
                        <button onClick={() => navigate('/alerts/settings')} className="back-button">Alert Settings</button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="ai-dashboard-main">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading dashboard...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadDashboard} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && dashboard && (
                    <>
                        {/* Row 1: Key Metrics */}
                        <section className="metrics-row">
                            <h2>📊 AI Usage — Today</h2>
                            <div className="stats-grid">
                                <StatsCard title="AI Calls" value={dashboard.aiUsage.todayCalls} icon="⚡" color="blue" subtitle="Today" />
                                <StatsCard title="Tokens Used" value={dashboard.aiUsage.todayTokens} icon="🪙" color="purple" subtitle="Today" />
                                <StatsCard title="Unique Users" value={dashboard.aiUsage.todayUniqueUsers} icon="👤" color="green" subtitle="Today" />
                                <StatsCard title="Pending Mod" value={dashboard.omoreInteractions.pendingModeration} icon="🛡️" color="red" subtitle="Flagged messages" />
                            </div>

                            <h2 className="section-label">📈 This Week</h2>
                            <div className="stats-grid">
                                <StatsCard title="Total Calls" value={dashboard.aiUsage.weekCalls} icon="📞" color="blue" subtitle="Last 7 days" />
                                <StatsCard title="Total Tokens" value={dashboard.aiUsage.weekTokens} icon="🪙" color="purple" subtitle="Last 7 days" />
                                <StatsCard title="Failure Rate" value={`${dashboard.aiUsage.failureRate.toFixed(1)}%`} icon="⚠️" color="yellow" subtitle="Failed API calls" />
                                <StatsCard title="Top Feature" value={(AIFeatureCategoryInfo as any)[dashboard.aiUsage.topCategory]?.displayName || dashboard.aiUsage.topCategory} icon="🏆" color="green" subtitle="Most called today" />
                            </div>
                        </section>

                        {/* Row 2: Category breakdown */}
                        <section className="category-section">
                            <div className="section-header">
                                <h2>🥧 AI Usage by Category</h2>
                                <button onClick={() => navigate('/ai/usage')} className="detail-link">View Full Analytics →</button>
                            </div>

                            <div className="category-layout">
                                {/* Pie chart via CSS conic-gradient */}
                                <div className="pie-chart-wrapper">
                                    <div
                                        className="pie-chart"
                                        style={{ background: buildPieGradient(dashboard.aiUsage.categoryBreakdown) }}
                                    ></div>
                                    <div className="pie-legend">
                                        {dashboard.aiUsage.categoryBreakdown.map(cat => (
                                            <div key={cat.category} className="legend-item">
                                                <span className="legend-swatch" style={{ background: cat.color }}></span>
                                                <span className="legend-label">
                                                    {(AIFeatureCategoryInfo as any)[cat.category]?.icon || ''} {(AIFeatureCategoryInfo as any)[cat.category]?.displayName || cat.category}
                                                </span>
                                                <span className="legend-value">{cat.calls}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="category-table-wrapper">
                                    <table className="category-table">
                                        <thead>
                                            <tr>
                                                <th>Category</th>
                                                <th>Calls</th>
                                                <th>Tokens</th>
                                                <th>Share</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dashboard.aiUsage.categoryBreakdown.map(cat => {
                                                const totalCalls = dashboard.aiUsage.categoryBreakdown.reduce((s, c) => s + c.calls, 0);
                                                const pct = totalCalls > 0 ? ((cat.calls / totalCalls) * 100).toFixed(1) : '0.0';
                                                return (
                                                    <tr key={cat.category}>
                                                        <td className="cat-name">
                                                            <span className="cat-icon">{(AIFeatureCategoryInfo as any)[cat.category]?.icon || ''}</span>
                                                            {(AIFeatureCategoryInfo as any)[cat.category]?.displayName || cat.category}
                                                        </td>
                                                        <td>{cat.calls.toLocaleString()}</td>
                                                        <td>{cat.tokens.toLocaleString()}</td>
                                                        <td>
                                                            <div className="bar-track">
                                                                <div className="bar-fill" style={{ width: `${pct}%`, background: cat.color }}></div>
                                                            </div>
                                                            <span className="bar-label">{pct}%</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>

                        {/* Row 3: Trends */}
                        <section className="trends-section">
                            <div className="section-header">
                                <h2>📈 AI Usage Trends (Last 7 Days)</h2>
                                <div className="toggle-group">
                                    <button
                                        className={`toggle-btn ${trendView === 'calls' ? 'active' : ''}`}
                                        onClick={() => setTrendView('calls')}
                                    >
                                        Calls
                                    </button>
                                    <button
                                        className={`toggle-btn ${trendView === 'tokens' ? 'active' : ''}`}
                                        onClick={() => setTrendView('tokens')}
                                    >
                                        Tokens
                                    </button>
                                </div>
                            </div>

                            <div className="bar-chart">
                                {trendData.map((point) => {
                                    const heightPct = trendMax > 0 ? (point.count / trendMax) * 100 : 0;
                                    return (
                                        <div key={point.date} className="bar-column">
                                            <span className="bar-value">{point.count.toLocaleString()}</span>
                                            <div className="bar-cell">
                                                <div
                                                    className="bar-body"
                                                    style={{
                                                        height: `${heightPct}%`,
                                                        background: trendView === 'calls' ? '#667eea' : '#9f7aea',
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="bar-date">{point.date.slice(5)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Row 4: Omore Quick Stats */}
                        <section className="omore-quick-section">
                            <div className="section-header">
                                <h2>🤖 Ask Omore — Quick Stats</h2>
                                <button onClick={() => navigate('/omore')} className="detail-link">View Omore Details →</button>
                            </div>

                            <div className="omore-stats-grid">
                                <StatsCard title="Sessions Today" value={dashboard.omoreInteractions.todaySessions} icon="💬" color="blue" subtitle="Active conversations" />
                                <StatsCard title="Messages Today" value={dashboard.omoreInteractions.todayMessages} icon="📝" color="green" subtitle="Total messages" />
                                <StatsCard title="Suggestion Rate" value={`${dashboard.omoreInteractions.suggestionAdoptionRate}%`} icon="✅" color="green" subtitle="Adoption this week" />
                                <StatsCard title="Users Flagged" value={dashboard.omoreInteractions.usersWithFlags} icon="⚠️" color="red" subtitle="Unique users" />
                            </div>

                            <div className="omore-details-row">
                                <div className="omore-detail-card">
                                    <h4>Top Helper Type</h4>
                                    <p className="detail-value">{dashboard.omoreInteractions.topHelperType}</p>
                                    <p className="detail-sub">Most popular this week</p>
                                </div>
                                <div className="omore-detail-card">
                                    <h4>Pending Moderation</h4>
                                    <p className="detail-value" style={{ color: dashboard.omoreInteractions.pendingModeration > 0 ? '#f56565' : '#48bb78' }}>
                                        {dashboard.omoreInteractions.pendingModeration}
                                    </p>
                                    <button onClick={() => navigate('/omore/moderation')} className="detail-link">Review Queue →</button>
                                </div>
                                <div className="omore-detail-card sessions-trend-card">
                                    <h4>Sessions Trend</h4>
                                    <div className="sparkline">
                                        {dashboard.omoreInteractions.sessionsTrend.map((point) => {
                                            const h = omoreTrendMax > 0 ? (point.count / omoreTrendMax) * 100 : 0;
                                            return (
                                                <div key={point.date} className="spark-bar">
                                                    <div className="spark-fill" style={{ height: `${h}%` }}></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </div>

            <AlertPanel />
        </div>
    );
};

// ─── Utility: build conic-gradient string for pie chart ──────

function buildPieGradient(breakdown: Array<{ category: string; calls: number; color: string }>): string {
    const total = breakdown.reduce((s, c) => s + c.calls, 0);
    if (total === 0) return '#e2e8f0';

    let cumulative = 0;
    const segments = breakdown.map(item => {
        const startDeg = (cumulative / total) * 360;
        cumulative += item.calls;
        const endDeg = (cumulative / total) * 360;
        return `${item.color} ${startDeg}deg ${endDeg}deg`;
    });

    return `conic-gradient(from 0deg, ${segments.join(', ')})`;
}
